import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";

import { socket } from "../../socket";

import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useProveedorPermissions } from "../../hooks/useProveedorPermissions"; // CHINA

import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Chip,
  useMediaQuery,
  InputAdornment,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DataGridPro } from "@mui/x-data-grid-pro";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";

import NotasBadge from "./NotasBadge";
import CpEntityChat, {
  getCpChatMetaKey,
  getCpChatMetaKeyFromRow,
} from "./CpEntityChat";
import CpChatDot from "./CpChatDot";
import {
  getCpNotasCountKeyFromApiRow,
  getCpNotasEntityKey,
} from "../../utils/cpNotasEntityKey";
import { getEntityChatSummary } from "../../api/Chat/entityChatApi";
import { setBrowserTabBadge } from "../../utils/browserTabBadge";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SearchIcon from "@mui/icons-material/Search";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pageMobileCardSx,
  pageSectionTitleSx,
} from "../../styles/pageModernStyles";

import {
  getCriticalPathHorizontal1,
  getCriticalPathClosedEntities,
  setCriticalPathEntityClosed,
} from "../../api/Estimates/estimatesApi";

import {
  getNotasByEntityWithUser,
  getNotasCountAll,
} from "../../api/CriticalPath/criticalPathNotasApi";

import {
  getFileContent,
  getMuestraFiles,
  getMuestraFileContent,
} from "../../api/Proyectos/projectFilesApi";

import CollapsibleHeaderGroup, {
  COLLAPSIBLE_GROUPS,
} from "./CollapsibleHeaderGroup";

import {
  LOGISTICS_DATE_FIELDS,
  compareNullableTimestamps,
  getLogisticsSortTimestamp,
  getTimelineColumnSortTimestamp,
  pickTimelineCell,
} from "./criticalPathSortUtils";

import CriticalPathDrawer from "./CriticalPathDrawer"; // Asegúrate de que la ruta es correcta

import {
  getEntityInitial,
  getSupplierColor,
  getClientColor,
} from "./criticalPathAvatars";
import { getCpDesignDisplay } from "./criticalPathDesignDisplay";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";
import {
  getClosedEntityKey,
  getCriticalPathDataGridRowId,
  parseIdPresupuestoA3PedidoFromRowId,
  parseIdPresupuestoFromRowId,
  prodRowsExcludingMuestras,
  withLegacyLogisticsFlag,
} from "./criticalPathEntityUtils";
import { applyTimelineTasksToCpRow } from "./criticalPathTimelineUtils";

dayjs.extend(customParseFormat);

const MOBILE_RENDER_CHUNK = 30;

// Cache global: persiste entre renders
const imagesCache = new Map();

const PLACEHOLDER_PREFIX = "__placeholder_";

/** Columnas de logística cuyo valor debe mostrarse como fecha (el resto como texto). */
const getHeaderColor = (taskName, mode) => {
  const yellowTasks = [
    "SMS comments",
    "SSS Fitting send",
    "SSS Fitting comments",
    "PP send",
    "Ok production Received",
    "Testing send to lab",
    "TOP sple send",
    "TOP approved",
  ];
  const blueTasks = ["Fabrics and trims In-house", "Production Starts"];
  const redTasks = ["Inspection date", "ExFD", "ETDF", "CUT-OFF", "ETD", "DD"];
  const greenTasks = [
    "POReference",
    "PODate",
    "Factory",
    /*  "Remarks", */
    "NCartons",
    "GrossWeightKgs",
    "TotalVolumeCbm",
    "ShipmentMethod",
    "BookingDone",
    "Forwarder",
    "ShipmentCodeCustomer",
    "BLAWBNr",
    "ETA",
    "DocumentsApproved",
    "OriginalTelexDocsSemt",
  ];

  const task = taskName.replace(PLACEHOLDER_PREFIX, "");

  if (greenTasks.includes(task))
    return mode === "dark" ? "#2e7d3233" : "#ffac7e3d";
  if (blueTasks.includes(task))
    return mode === "dark" ? "#1565c033" : "#90b2b74d";
  if (yellowTasks.includes(task))
    return mode === "dark" ? "#fdd83533" : "#fff9c4a3";
  if (redTasks.includes(task))
    return mode === "dark" ? "#e5393533" : "#d4ff305c";

  return mode === "dark" ? "#eeeeee" : "#eeeeee"; // Placeholder default
};

const getLogisticsFieldValue = (row, field) => {
  if (!Array.isArray(row?.logistics)) return "";

  return row.logistics
    .map((entry) => {
      const value = entry?.[field];
      if (!value) return null;

      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = dayjs(value);
        return d.isValid() ? d.format("DD/MM/YYYY") : null;
      }

      return value;
    })
    .filter((v) => v != null && v !== "")
    .join("\n");
};

const TaskCell = React.memo(function TaskCell({
  row,
  task,
  taskObj,
  isLogistics,
  isDark,
}) {
  const { t } = useTranslation();
  // Los hooks SIEMPRE deben ejecutarse, aunque taskObj sea null.
  const diff1 = taskObj?.ExpectedUpdated;
  const diff2 = taskObj?.ExpectedReal;
  const diff = diff2 ?? diff1;

  const displayValue = useMemo(() => {
    // 🟢 LOGÍSTICA (SIEMPRE)
    if (isLogistics) {
      const values = row._logisticsMap?.[task];

      if (Array.isArray(values) && values.length > 0) {
        return values
          .map((v) => {
            if (!LOGISTICS_DATE_FIELDS.has(task)) return String(v);
            const d = dayjs(v);
            return d.isValid() ? d.format("DD/MM/YYYY") : String(v);
          })
          .join("\n");
      }

      return "";
    }

    // 🟢 TIMELINE
    if (taskObj?.RealDate) return dayjs(taskObj.RealDate).format("DD/MM/YYYY");

    if (taskObj?.UpdatedDate)
      return dayjs(taskObj.UpdatedDate).format("DD/MM/YYYY");

    if (taskObj?.ExpectedDate)
      return dayjs(taskObj.ExpectedDate).format("DD/MM/YYYY");

    return "";
  }, [row, task, taskObj, isLogistics]);

  const expected = useMemo(() => {
    if (!taskObj?.ExpectedDate) return null;
    return dayjs(taskObj.ExpectedDate).format("DD/MM");
  }, [taskObj?.ExpectedDate]);

  const textColor = !taskObj?.RealDate && diff != null ? "#00ff16" : undefined;

  const icon = useMemo(() => {
    if (diff == null) return null;

    if (diff <= -1)
      return <CheckCircleIcon sx={{ color: "#c4d900", fontSize: 16 }} />;
    if (diff <= 0)
      return <CheckCircleIcon sx={{ color: "#00c2a3", fontSize: 16 }} />;
    if (diff <= 3) return <ErrorIcon sx={{ color: "orange", fontSize: 16 }} />;
    if (diff <= 6) return <ErrorIcon sx={{ color: "brown", fontSize: 16 }} />;

    return <WarningIcon sx={{ color: "red", fontSize: 16 }} />;
  }, [diff]);

  const tooltipContent = useMemo(() => {
    if (!taskObj) return null;

    return (
      <>
        {taskObj.ExpectedDate && (
          <div>
            <strong>{t("CriticalPathHorizontal.taskExpected")}</strong>{" "}
            {dayjs(taskObj.ExpectedDate).format("DD/MM/YYYY")}
          </div>
        )}
        {taskObj.UpdatedDate && (
          <div>
            <strong>{t("CriticalPathHorizontal.taskUpdated")}</strong>{" "}
            {dayjs(taskObj.UpdatedDate).format("DD/MM/YYYY")}
          </div>
        )}
        {taskObj.RealDate && (
          <div>
            <strong>{t("CriticalPathHorizontal.taskReal")}</strong>{" "}
            {dayjs(taskObj.RealDate).format("DD/MM/YYYY")}
          </div>
        )}
      </>
    );
  }, [taskObj, t]);

  // AHORA hacemos el return condicional,
  // PERO **después** de haber llamado a los hooks.
  /*   if (!taskObj) return null; */

  return (
    <Box
      sx={{
        whiteSpace: "pre-line",
        width: "100%",
        height: "100%",
        backgroundColor: "transparent",
        fontSize: ".8rem",
        color: textColor ?? (isLogistics && isDark ? "#fff" : undefined),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Tooltip title={tooltipContent} arrow placement="top">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
          >
            {icon}
            <Typography sx={{ fontWeight: "bold", fontSize: ".75rem" }}>
              {displayValue}
            </Typography>
          </Box>
        </Tooltip>

        {expected && !displayValue.includes(expected) && (
          <Typography sx={{ fontSize: ".6rem", color: "#999" }}>
            {expected}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

const PhotosCell = React.memo(function PhotosCell({
  productId,
  idMuestra,
  numeroPresupuesto,
  onImageClick,
}) {
  const [images, setImages] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const cellRef = useRef(null);

  const cacheKey = productId
    ? `P_${numeroPresupuesto}`
    : idMuestra
      ? `M_${idMuestra}`
      : null;

  useEffect(() => {
    const el = cellRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "320px 0px", threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!cacheKey || !isVisible) return;

    if (imagesCache.has(cacheKey)) {
      setImages(imagesCache.get(cacheKey));
      return;
    }

    let active = true;

    const load = async () => {
      try {
        let formatted = [];

        // 🟢 PRODUCCIÓN
        if (productId && numeroPresupuesto) {
          const files = await getFileContent(numeroPresupuesto);

          formatted = files.map((file) => ({
            url: `data:${file.mimeType};base64,${file.data}`,
            fileName: file.fileName,
          }));
        }

        // 🟣 MUESTRAS
        if (idMuestra) {
          const files = await getMuestraFiles(idMuestra);

          const images = await Promise.all(
            files.map(async (file) => {
              const content = await getMuestraFileContent(file.path);

              return {
                url: `data:${content.mimeType};base64,${content.data}`,
                fileName: file.name,
              };
            }),
          );

          formatted = images;
        }

        imagesCache.set(cacheKey, formatted);

        if (active) setImages(formatted);
      } catch {
        imagesCache.set(cacheKey, []);
        if (active) setImages([]);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [productId, idMuestra, numeroPresupuesto, cacheKey, isVisible]);

  if (images === null) {
    return (
      <Box
        ref={cellRef}
        sx={{
          width: 50,
          height: 50,
          ml: 1,
          background: "#ddd",
          borderRadius: 1,
        }}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {images.slice(0, 2).map((img, idx) => (
        <Box
          key={idx}
          component="img"
          src={img.url}
          alt={img.fileName}
          onClick={(e) => {
            e.stopPropagation();
            onImageClick(e.currentTarget, img.url);
          }}
          sx={{
            width: 50,
            height: 50,
            objectFit: "cover",
            borderRadius: 1,
            cursor: "pointer",
            ml: 1,
          }}
        />
      ))}
    </Box>
  );
});

/** Claves internas de `COLLAPSIBLE_GROUPS` → sufijo i18n bajo `CriticalPathHorizontal.groups`. */
const GROUP_ID_TO_I18N = {
  "Muestras y aprobaciones": "samplesApprovals",
  Producción: "production",
  "Fechas críticas": "criticalDates",
  "Envío y logística": "shippingLogistics",
};

const CriticalPathHorizontal = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const numberLocale = numberLocaleFromI18n(i18n.language);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { contentMargin } = useContext(SidebarContext);
  const accent = theme.palette.primary.main;

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // 🧠 Estado de visibilidad con persistencia local
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    try {
      const saved = localStorage.getItem("criticalPathColumnVisibility");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Un solo drawer como CriticalPathHorizontal1: "PRODUCCION" | "MUESTRA" */
  const [drawerMode, setDrawerMode] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedIdPresupuesto, setSelectedIdPresupuesto] = useState(null);
  const [selectedIdPresupuestoA3Pedido, setSelectedIdPresupuestoA3Pedido] =
    useState(null);
  const [selectedGridRowSnapshot, setSelectedGridRowSnapshot] = useState(null);

  const [selectedMuestraId, setSelectedMuestraId] = useState(null);
  const [selectedLegacyLogistics, setSelectedLegacyLogistics] = useState([]);
  /** Solo edición legacy en el drawer si la fila ya tenía ProductionLogistics. */
  const [selectedHasLegacyLogistics, setSelectedHasLegacyLogistics] =
    useState(false);

  /* fotos */

  const [anchorEl, setAnchorEl] = useState(null);
  const [hoveredImage, setHoveredImage] = useState(null);

  // Chat + Notas (unified drawer)
  const [cpChatState, setCpChatState] = useState({
    open: false,
    productId: null,
    idPresupuestoA3Pedido: null,
    title: "",
    subtitle: "",
    initialTab: "notas",
  });
  const [chatMetaMap, setChatMetaMap] = useState({});
  const [closeConfirmState, setCloseConfirmState] = useState({
    open: false,
    row: null,
  });

  const loadChatSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      const { userId } = jwtDecode(token);
      const [prodSummary, pedidoSummary] = await Promise.all([
        getEntityChatSummary("Producto", userId),
        getEntityChatSummary("PedidoA3", userId),
      ]);
      const normalized = {};
      Object.entries(prodSummary || {}).forEach(([id, meta]) => {
        normalized[`Producto:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      Object.entries(pedidoSummary || {}).forEach(([id, meta]) => {
        normalized[`PedidoA3:${Number(id)}`] = {
          total: Number(meta.total) || 0,
          noLeidos: Number(meta.noLeidos) || 0,
          hasMessages: (Number(meta.total) || 0) > 0,
          hasMention: false,
        };
      });
      setChatMetaMap(normalized);
    } catch (error) {
      console.error("Error loading CP chat summary:", error);
    }
  }, []);

  const clearChatUnreadForKey = useCallback((metaKey) => {
    if (!metaKey) return;
    setChatMetaMap((prev) => {
      const cur = prev[metaKey];
      if (!cur) return prev;
      const next = {
        ...prev,
        [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
      };
      const anyMention = Object.values(next).some((m) => m?.hasMention);
      if (!anyMention) setBrowserTabBadge(false);
      return next;
    });
  }, []);

  const openCpChatFromRow = useCallback(
    (row, tab = "mensajes") => {
      if (!row) return;
      const productId = row.productId ?? row.ProductId ?? null;
      const idPresupuestoA3Pedido = row.IdPresupuestoA3Pedido ?? null;
      const d = getCpDesignDisplay(row);
      setCpChatState({
        open: true,
        productId,
        idPresupuestoA3Pedido,
        title:
          row.designCliente ||
          d?.designCliente ||
          (idPresupuestoA3Pedido
            ? `Pedido ${idPresupuestoA3Pedido}`
            : `Product ${productId || ""}`),
        subtitle: d?.disenoMuestra || "",
        initialTab: tab,
      });
      clearChatUnreadForKey(
        getCpChatMetaKey({ productId, idPresupuestoA3Pedido }),
      );
    },
    [clearChatUnreadForKey],
  );

  const getRowChatMeta = useCallback(
    (row) => {
      const key = getCpChatMetaKeyFromRow(row);
      const meta = (key && chatMetaMap[key]) || {};
      return {
        total: Number(meta.total) || 0,
        noLeidos: Number(meta.noLeidos) || 0,
        hasMention: Boolean(meta.hasMention),
        hasMessages:
          Boolean(meta.hasMessages) || (Number(meta.total) || 0) > 0,
      };
    },
    [chatMetaMap],
  );

  const refreshCpNotasCount = async ({
    productId,
    idMuestra,
    idPresupuestoA3Pedido,
  }) => {
    try {
      const token = localStorage.getItem("token");
      const { userId } = jwtDecode(token);
      const notas = await getNotasByEntityWithUser({
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
        userId,
      });
      const key = getCpNotasEntityKey({
        productId,
        idMuestra,
        idPresupuestoA3Pedido,
      });
      if (!key) return;
      setRows((prev) =>
        prev.map((row) => {
          const rowKey = getCpNotasEntityKey({
            productId: row.productId,
            idMuestra: row.idMuestra,
            idPresupuestoA3Pedido: row.IdPresupuestoA3Pedido,
          });
          return rowKey === key
            ? { ...row, NotasCount: notas.length }
            : row;
        }),
      );
    } catch (e) {
      console.error("Error refrescando contador notas:", e);
    }
  };

  const handleOpenNotas = ({
    productId,
    idMuestra,
    idPresupuestoA3Pedido,
    title,
    subtitle,
  }) => {
    const key = getCpNotasEntityKey({
      productId,
      idMuestra,
      idPresupuestoA3Pedido,
    });
    const row = (rows || []).find((r) => {
      const rowKey = getCpNotasEntityKey({
        productId: r.productId,
        idMuestra: r.idMuestra,
        idPresupuestoA3Pedido: r.IdPresupuestoA3Pedido,
      });
      return rowKey && rowKey === key;
    });
    if (row) {
      openCpChatFromRow(row, "notas");
      return;
    }
    const d = row ? getCpDesignDisplay(row) : null;
    setCpChatState({
      open: true,
      productId: productId ?? null,
      idPresupuestoA3Pedido: idPresupuestoA3Pedido ?? null,
      title:
        title ||
        (idPresupuestoA3Pedido
          ? `Pedido ${idPresupuestoA3Pedido}`
          : `Product ${productId || ""}`),
      subtitle: subtitle || d?.disenoMuestra || "",
      initialTab: "notas",
    });
    clearChatUnreadForKey(
      getCpChatMetaKey({ productId, idPresupuestoA3Pedido }),
    );
  };

  const handleOpenChat = (row, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    openCpChatFromRow(row, "mensajes");
  };

  /* filtro terminados */
  const [showWithOriginalTelex, setShowWithOriginalTelex] = useState(false);
  const [showClosedEntities, setShowClosedEntities] = useState(false);

  // FILTRO GENERAL
  const [searchText, setSearchText] = useState("");
  const [legendAnchorEl, setLegendAnchorEl] = useState(null);
  const [mobileRenderedCount, setMobileRenderedCount] = useState(MOBILE_RENDER_CHUNK);
  const legendOpen = Boolean(legendAnchorEl);

  // 🇨🇳 CHINA — permisos por proveedor
  const { loading: proveedorLoading, filterByProveedor } =
    useProveedorPermissions();

  const filterFieldSx = pageFieldSx(theme);

  const legendItems = useMemo(
    () => [
      {
        color: "#c4d900",
        label: t("CriticalPathHorizontal.legendBeforeExpected"),
      },
      { color: "#00c2a3", label: t("CriticalPathHorizontal.legendOnTime") },
      { color: "orange", label: t("CriticalPathHorizontal.legend3dDelay") },
      { color: "brown", label: t("CriticalPathHorizontal.legend6dDelay") },
      {
        color: "red",
        label: t("CriticalPathHorizontal.legendMoreThan6dDelay"),
      },
      {
        color: "#00ff16",
        label: t("CriticalPathHorizontal.legendWithoutRealTime"),
        textOnly: t("CriticalPathHorizontal.legendDelaySample"),
      },
    ],
    [t],
  );

  const GROUP_COLOR_MAP = {
    "Muestras y aprobaciones":
      theme.palette.mode === "dark" ? "#fdd83533" : "#fff9c4a3", // amarillo
    Producción: theme.palette.mode === "dark" ? "#1565c033" : "#90b2b74d", // azul
    "Fechas críticas":
      theme.palette.mode === "dark" ? "#e5393533" : "#d4ff305c", // rojo
    "Envío y logística":
      theme.palette.mode === "dark" ? "#2e7d3233" : "#ffac7e3d", // verde
  };

  const groupsWithPlaceholders = useMemo(
    () =>
      Object.entries(COLLAPSIBLE_GROUPS).reduce((acc, [group, fields]) => {
        const placeholder = PLACEHOLDER_PREFIX + group.replace(/\s+/g, "_");
        acc[group] = [
          placeholder,
          ...fields.filter((f) => !f.startsWith(PLACEHOLDER_PREFIX)),
        ];
        return acc;
      }, {}),
    [],
  );

  const defaultTaskOrder = useMemo(
    () => Object.values(groupsWithPlaceholders).flat(),
    [groupsWithPlaceholders],
  );

  // 🔧 Normaliza una línea de logística para que coincida con el grid
  const normalizeLogisticsEntry = (entry) => ({
    ...entry,

    // 🔁 Compatibilidad con nombres antiguos / backend
    GrossWeightKgs: entry.GrossWeightKgs ?? entry.GrossWeight,
    TotalVolumeCbm: entry.TotalVolumeCbm ?? entry.Volume,
    ShipmentMethod: entry.ShipmentMethod ?? entry["Shipment Method"],
  });

  const handleToggleEntityClosed = useCallback(async (row) => {
    try {
      const willClose = !Boolean(row?.IsClosed);
      await setCriticalPathEntityClosed({
        productId: row?.productId ?? null,
        idPresupuestoA3Pedido: row?.IdPresupuestoA3Pedido ?? null,
        idMuestra: row?.idMuestra ?? null,
        isClosed: willClose,
      });
      setRows((prev) =>
        prev
          .map((r) =>
            r.entityKey === row.entityKey ? { ...r, IsClosed: willClose } : r,
          )
          .filter((r) =>
            showClosedEntities ? Boolean(r.IsClosed) : !Boolean(r.IsClosed),
          ),
      );
    } catch (error) {
      console.error("❌ Error cerrando/reabriendo fila CP:", error);
    }
  }, [showClosedEntities]);

  const handleRequestToggleEntityClosed = useCallback(
    (row) => {
      if (!row) return;
      const willClose = !Boolean(row?.IsClosed);
      if (!willClose) {
        handleToggleEntityClosed(row);
        return;
      }
      setCloseConfirmState({ open: true, row });
    },
    [handleToggleEntityClosed],
  );

  const handleConfirmCloseEntity = useCallback(async () => {
    const rowToClose = closeConfirmState.row;
    setCloseConfirmState({ open: false, row: null });
    if (!rowToClose) return;
    await handleToggleEntityClosed(rowToClose);
  }, [closeConfirmState.row, handleToggleEntityClosed]);

  const fetchAndSetRows = useCallback(async ({ showSpinner = false } = {}) => {
    if (showSpinner) setIsLoading(true);
    try {
      const [prod, closedRaw] = await Promise.all([
        getCriticalPathHorizontal1(true),
        getCriticalPathClosedEntities().catch(() => []),
      ]);

      const closedSet = new Set(
        (Array.isArray(closedRaw) ? closedRaw : [])
          .map((x) => {
            return getClosedEntityKey(
              x?.ProductId,
              x?.IdMuestra,
              x?.IdPresupuestoA3Pedido,
            );
          })
          .filter(Boolean),
      );

      let merged = prodRowsExcludingMuestras(prod).map((r) => ({
        ...r,
        IsClosed: closedSet.has(getClosedEntityKey(r)),
      }));

      merged = filterByProveedor(merged);
      const filteredData = withLegacyLogisticsFlag(merged);

      // 🔵 UNA sola llamada para todos los contadores
      const notasCountData = await getNotasCountAll();

      const notasMap = {};

      notasCountData.forEach((r) => {
        const key = getCpNotasCountKeyFromApiRow(r);
        if (key) notasMap[key] = Number(r.total);
      });

      const logisticsFields =
        COLLAPSIBLE_GROUPS["Envío y logística"]?.filter(
          (f) => !String(f).startsWith("__placeholder")
        ) ?? [];

      const processedRows = filteredData.map((row) => {
        // Clave estable por FILA (presupuesto o muestra). NO usar solo P_${ProductId}:
        // varios presupuestos comparten IdProducto y colisionarían en entityKey → mismas fechas / toggles / keys React.
        const entityKey =
          row.id != null && String(row.id).trim() !== ""
            ? String(row.id)
            : row.ProductId != null
              ? `P_${Number(row.ProductId)}`
              : row.ID_MUESTRA != null
                ? `M_${Number(row.ID_MUESTRA)}`
                : `ROW_${Math.random()}`;

        const notasLookupKey = getCpNotasEntityKey({
          productId: row.ProductId,
          idMuestra: row.ID_MUESTRA,
          idPresupuestoA3Pedido: row.IdPresupuestoA3Pedido,
        });

        // 🟢 Construir _logisticsMap desde row.logistics para pintar columnas de logística
        const _logisticsMap = {};
        if (Array.isArray(row.logistics) && row.logistics.length > 0) {
          for (const field of logisticsFields) {
            const values = row.logistics
              .map((entry) => {
                const norm = normalizeLogisticsEntry(entry);
                return norm[field];
              })
              .filter((v) => v != null && v !== "");
            if (values.length > 0) _logisticsMap[field] = values;
          }
        }
        const hasLegacyLogistics =
          Array.isArray(row.logistics) &&
          row.logistics.some((entry) => !Boolean(entry?._fromLogisticaEnvio));

        return {
          ...row,
          entityKey,
          productId: row.ProductId ?? null,
          idMuestra: row.ID_MUESTRA ?? null,
          NotasCount: notasLookupKey ? notasMap[notasLookupKey] || 0 : 0,
          _logisticsMap,
          HasLegacyLogistics: hasLegacyLogistics,
        };
      });

      setRows(processedRows);
    } catch (error) {
      console.error("❌ Error al recargar datos del grid:", error);
    } finally {
      if (showSpinner) setIsLoading(false);
    }
  }, [filterByProveedor]);

  useEffect(() => {
    if (proveedorLoading) return;

    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
        return;
      }
    } catch {
      navigate("/login");
      return;
    }

    fetchAndSetRows({ showSpinner: true });
  }, [proveedorLoading, fetchAndSetRows, navigate]);

  // Solo añadir columnas nuevas al modelo; no resetear en cada render (rompía colapso y scroll).
  useEffect(() => {
    setColumnVisibilityModel((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of defaultTaskOrder) {
        if (next[field] === undefined) {
          next[field] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaultTaskOrder]);

  const refreshCounts = useCallback(async () => {
    try {
      const data = await getNotasCountAll();

      const counts = {};

      data.forEach((r) => {
        const key =
          r.ProductId != null
            ? `P_${Number(r.ProductId)}`
            : r.ID_MUESTRA != null
              ? `M_${Number(r.ID_MUESTRA)}`
              : null;

        if (key) {
          counts[key] = Number(r.total);
        }
      });

      setRows((prev) =>
        prev.map((row) => {
          const notasLookupKey = getCpNotasEntityKey({
            productId: row.productId,
            idMuestra: row.idMuestra,
            idPresupuestoA3Pedido: row.IdPresupuestoA3Pedido,
          });
          return {
            ...row,
            NotasCount: notasLookupKey ? counts[notasLookupKey] || 0 : 0,
          };
        }),
      );
    } catch (e) {
      console.error("Error refreshing notas:", e);
    }
  }, []);

  useEffect(() => {
    loadChatSummary();
  }, [loadChatSummary]);

  useEffect(() => {
    socket.on("criticalpath-nota-count-updated", refreshCounts);

    return () => {
      socket.off("criticalpath-nota-count-updated", refreshCounts);
    };
  }, [refreshCounts]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const currentUserId = token ? jwtDecode(token)?.userId : null;
    const openKey = cpChatState.open
      ? getCpChatMetaKey({
          productId: cpChatState.productId,
          idPresupuestoA3Pedido: cpChatState.idPresupuestoA3Pedido,
        })
      : null;

    const handleChatNuevo = ({
      entityType,
      entityId,
      mentionedUserIds,
      mensaje,
    }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      const openHere = Boolean(openKey && openKey === metaKey);
      const isMentioned =
        Number(mensaje?.AutorId) !== Number(currentUserId) &&
        (mentionedUserIds || []).map(Number).includes(Number(currentUserId));

      setChatMetaMap((prev) => {
        const cur = prev[metaKey] || {
          total: 0,
          noLeidos: 0,
          hasMention: false,
        };
        return {
          ...prev,
          [metaKey]: {
            total: (cur.total || 0) + 1,
            noLeidos: openHere ? 0 : (cur.noLeidos || 0) + 1,
            hasMessages: true,
            hasMention: openHere
              ? false
              : Boolean(cur.hasMention) || isMentioned,
          },
        };
      });

      if (isMentioned && !openHere) {
        setBrowserTabBadge(true);
      }
    };

    const handleChatLeidos = ({ entityType, entityId, userId: readBy }) => {
      if (entityType !== "Producto" && entityType !== "PedidoA3") return;
      if (
        readBy != null &&
        currentUserId != null &&
        Number(readBy) !== Number(currentUserId)
      ) {
        return;
      }
      const metaKey = getCpChatMetaKey({ entityType, entityId });
      if (!metaKey) return;
      setChatMetaMap((prev) => {
        const cur = prev[metaKey];
        if (!cur) return prev;
        return {
          ...prev,
          [metaKey]: { ...cur, noLeidos: 0, hasMention: false },
        };
      });
      setBrowserTabBadge(false);
    };

    socket.on("chat-nuevo-mensaje", handleChatNuevo);
    socket.on("chat-mensajes-leidos", handleChatLeidos);
    return () => {
      socket.off("chat-nuevo-mensaje", handleChatNuevo);
      socket.off("chat-mensajes-leidos", handleChatLeidos);
    };
  }, [
    cpChatState.open,
    cpChatState.productId,
    cpChatState.idPresupuestoA3Pedido,
  ]);

  const timelineGridRefreshRef = useRef(null);
  useEffect(() => {
    const scheduleGridRefresh = () => {
      if (timelineGridRefreshRef.current) {
        clearTimeout(timelineGridRefreshRef.current);
      }
      timelineGridRefreshRef.current = setTimeout(() => {
        timelineGridRefreshRef.current = null;
        fetchAndSetRows();
      }, 350);
    };

    socket.on("timelineUpdated", scheduleGridRefresh);
    return () => {
      socket.off("timelineUpdated", scheduleGridRefresh);
      if (timelineGridRefreshRef.current) {
        clearTimeout(timelineGridRefreshRef.current);
        timelineGridRefreshRef.current = null;
      }
    };
  }, [fetchAndSetRows]);

  /* filtro */
  const filteredRows = useMemo(() => {
    let result = [...rows];

    // 🔹 Filtro proyectos terminados
    if (!showWithOriginalTelex) {
      result = result.filter((row) => {
        const hasTelexDate =
          Array.isArray(row.logistics) &&
          row.logistics.some(
            (entry) =>
              entry?.OriginalTelexDocsSemt &&
              dayjs(entry.OriginalTelexDocsSemt).isValid(),
          );

        return !hasTelexDate;
      });
    }

    result = result.filter((row) =>
      showClosedEntities ? Boolean(row?.IsClosed) : !Boolean(row?.IsClosed),
    );

    // 🔎 🔎 🔎 NUEVO FILTRO DE BÚSQUEDA
    if (searchText.trim() !== "") {
      const search = searchText.toLowerCase();

      result = result.filter((row) => {
        const d = getCpDesignDisplay(row);
        return (
          row.CLiente?.toLowerCase().includes(search) ||
          row.ProveedorNombre?.toLowerCase().includes(search) ||
          row.designCliente?.toLowerCase().includes(search) ||
          row.DiseñoMuestra?.toLowerCase().includes(search) ||
          d.disenoMuestra?.toLowerCase().includes(search) ||
          d.referenciaPedido?.toLowerCase().includes(search) ||
          String(row.IdPedv ?? "").includes(search) ||
          row.MarcaNombre?.toLowerCase().includes(search) ||
          row.NumeroPresupuesto?.toString().includes(search)
        );
      });
    }

    return result;
  }, [rows, showWithOriginalTelex, searchText, showClosedEntities]);

  useEffect(() => {
    setMobileRenderedCount(MOBILE_RENDER_CHUNK);
  }, [
    searchText,
    showWithOriginalTelex,
    showClosedEntities,
    rows.length,
  ]);

  const handleOpenGridRow = useCallback(
    (row, stableId = "") => {
      if (!row) return;

      const rowPid = row?.ProductId ?? row?.productId;
      const rowMid = row?.ID_MUESTRA ?? row?.idMuestra;
      if (
        (rowPid == null || String(rowPid).trim() === "") &&
        (rowMid == null || String(rowMid).trim() === "")
      ) {
        return;
      }

      const rowIdForCp =
        row.id != null && String(row.id).trim() !== ""
          ? String(row.id)
          : stableId || getCriticalPathDataGridRowId(row);

      if (rowMid != null && rowMid !== "") {
        if (
          drawerOpen &&
          drawerMode === "MUESTRA" &&
          Number(rowMid) === Number(selectedMuestraId)
        ) {
          return;
        }
        setDrawerMode("MUESTRA");
        setSelectedProductId(null);
        setSelectedMuestraId(Number(rowMid));
        setSelectedIdPresupuesto(null);
        setSelectedIdPresupuestoA3Pedido(null);
        setSelectedGridRowSnapshot(null);
        setSelectedLegacyLogistics([]);
        setSelectedHasLegacyLogistics(false);
        setDrawerOpen(true);
        return;
      }

      const idPresupuestoRow =
        row.idPresupuestoCRM != null &&
        Number.isFinite(Number(row.idPresupuestoCRM))
          ? Number(row.idPresupuestoCRM)
          : parseIdPresupuestoFromRowId(rowIdForCp);

      const idPedidoRow =
        row.IdPresupuestoA3Pedido != null &&
        Number.isFinite(Number(row.IdPresupuestoA3Pedido))
          ? Number(row.IdPresupuestoA3Pedido)
          : parseIdPresupuestoA3PedidoFromRowId(rowIdForCp);

      if (
        drawerOpen &&
        drawerMode === "PRODUCCION" &&
        (row.ProductId ?? row.productId) === selectedProductId &&
        idPresupuestoRow === selectedIdPresupuesto &&
        idPedidoRow === selectedIdPresupuestoA3Pedido
      ) {
        return;
      }

      setDrawerMode("PRODUCCION");
      setSelectedProductId(row.ProductId ?? row.productId ?? null);
      setSelectedMuestraId(null);
      setSelectedIdPresupuesto(idPresupuestoRow);
      setSelectedIdPresupuestoA3Pedido(idPedidoRow);
      setSelectedGridRowSnapshot(row);
      setSelectedLegacyLogistics(
        Array.isArray(row?.logistics)
          ? row.logistics.filter((l) => !Boolean(l?._fromLogisticaEnvio))
          : [],
      );
      setSelectedHasLegacyLogistics(Boolean(row?.HasLegacyLogistics));
      setDrawerOpen(true);
    },
    [
      drawerOpen,
      drawerMode,
      selectedMuestraId,
      selectedProductId,
      selectedIdPresupuesto,
      selectedIdPresupuestoA3Pedido,
    ],
  );

  const isMobileRowActive = useCallback(
    (row) => {
      const rowMid = row?.ID_MUESTRA ?? row?.idMuestra;
      if (rowMid != null && rowMid !== "") {
        return (
          drawerOpen &&
          drawerMode === "MUESTRA" &&
          Number(rowMid) === Number(selectedMuestraId)
        );
      }
      const rowIdForCp = getCriticalPathDataGridRowId(row);
      const idPresupuestoRow =
        row.idPresupuestoCRM != null &&
        Number.isFinite(Number(row.idPresupuestoCRM))
          ? Number(row.idPresupuestoCRM)
          : parseIdPresupuestoFromRowId(rowIdForCp);
      const idPedidoRow =
        row.IdPresupuestoA3Pedido != null &&
        Number.isFinite(Number(row.IdPresupuestoA3Pedido))
          ? Number(row.IdPresupuestoA3Pedido)
          : parseIdPresupuestoA3PedidoFromRowId(rowIdForCp);
      return (
        drawerOpen &&
        drawerMode === "PRODUCCION" &&
        (row.ProductId ?? row.productId) === selectedProductId &&
        idPresupuestoRow === selectedIdPresupuesto &&
        idPedidoRow === selectedIdPresupuestoA3Pedido
      );
    },
    [
      drawerOpen,
      drawerMode,
      selectedMuestraId,
      selectedProductId,
      selectedIdPresupuesto,
      selectedIdPresupuestoA3Pedido,
    ],
  );

  const isDark = theme.palette.mode === "dark";

  const handleImageClick = (anchor, imageUrl) => {
    setAnchorEl(anchor);
    setHoveredImage(imageUrl);
  };
  const fixedColumns = useMemo(
    () => [
      {
        field: "Chat",
        headerName: "",
        width: 34,
        minWidth: 34,
        sortable: false,
        pinned: "left",
        cellClassName: "cell-chat",
        renderCell: (params) => {
          const { noLeidos, hasMessages, hasMention } = getRowChatMeta(
            params.row,
          );
          return (
            <CpChatDot
              noLeidos={noLeidos}
              hasMessages={hasMessages}
              hasMention={hasMention}
              onClick={(e) => handleOpenChat(params.row, e)}
            />
          );
        },
      },
      {
        field: "Notas",
        headerName: "",
        width: 28,
        minWidth: 28,
        sortable: false,
        pinned: "left",
        cellClassName: "cell-notas",
        renderCell: (params) => (
          <Box
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{ cursor: "pointer" }}
          >
            <NotasBadge
              key={`${params.row.entityKey}-${params.row.NotasCount}`}
              productId={params.row.productId}
              idMuestra={params.row.idMuestra}
              idPresupuestoA3Pedido={params.row.IdPresupuestoA3Pedido}
              notasCount={params.row.NotasCount || 0}
              onOpenDialog={handleOpenNotas}
            />
          </Box>
        ),
      },
      {
        field: "cerrar",
        headerName: "",
        width: 42,
        minWidth: 42,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        pinned: "left",
        renderCell: (params) => (
          <Tooltip
            title={
              params.row?.IsClosed
                ? t("CriticalPathHorizontal.tooltipReopenCp")
                : t("CriticalPathHorizontal.tooltipCloseCp")
            }
          >
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                handleRequestToggleEntityClosed(params.row);
              }}
              sx={{
                color: params.row?.IsClosed ? "error.main" : "success.main",
              }}
            >
              {params.row?.IsClosed ? (
                <VisibilityIcon fontSize="inherit" />
              ) : (
                <VisibilityOffIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "legacyLog",
        headerName: "",
        width: 34,
        minWidth: 34,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        pinned: "left",
        renderCell: (params) =>
          params.row?.HasLegacyLogistics ? (
            <Tooltip
              title={t("CriticalPathHorizontal.tooltipLegacyLogistics")}
            >
              <LocalShippingIcon sx={{ fontSize: 16, color: "warning.main" }} />
            </Tooltip>
          ) : null,
      },
      {
        field: "CLiente",
        headerName: t("CriticalPathHorizontal.gridHeaderClient"),
        width: 80,
        pinned: "left",
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const clientName =
            params.value || t("CriticalPathHorizontal.noClient");
          const clientAlias = params.row?.MarcaNombre || "";
          const aliasIsDifferent =
            clientAlias &&
            clientAlias.trim().toUpperCase() !==
              String(clientName).trim().toUpperCase();
          const clientColor = getClientColor(clientName);
          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <>
                  <strong>{clientName}</strong>
                  {aliasIsDifferent ? (
                    <>
                      <br />
                      <em>
                        {t("CriticalPathHorizontal.aliasPrefix")}{" "}
                        {clientAlias}
                      </em>
                    </>
                  ) : null}
                </>
              }
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    letterSpacing: 0.2,
                    color: "#fff",
                    backgroundColor: clientColor,
                    boxShadow: `0 2px 6px ${alpha(clientColor, 0.35)}`,
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    cursor: "default",
                    userSelect: "none",
                    flexShrink: 0,
                  }}
                >
                  {getEntityInitial(clientName)}
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "TemporadaNombre",
        headerName: t("CriticalPathHorizontal.gridHeaderSeason"),
        width: 80,
        pinned: "left",
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.7rem", pt: 0.8, pl: 1 }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "ProveedorNombre",
        headerName: t("CriticalPathHorizontal.gridHeaderSupplier"),
        width: 80,
        pinned: "left",
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const supplierName =
            params.value || t("CriticalPathHorizontal.noSupplier");
          const supplierColor = getSupplierColor(supplierName);
          return (
            <Tooltip
              arrow
              placement="top"
              title={<strong>{supplierName}</strong>}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    letterSpacing: 0.2,
                    color: "#fff",
                    backgroundColor: supplierColor,
                    boxShadow: `0 2px 6px ${alpha(supplierColor, 0.35)}`,
                    border: `1px solid ${alpha("#000", 0.08)}`,
                    cursor: "default",
                    userSelect: "none",
                    flexShrink: 0,
                  }}
                >
                  {getEntityInitial(supplierName)}
                </Box>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "designCliente",
        headerName: t("CriticalPathHorizontal.gridHeaderDesignStage"),
        width: 220,
        pinned: "left",
        renderCell: (params) => {
          const d = getCpDesignDisplay(params.row);
          const tooltip = (
            <>
              <strong>{d.designCliente || t("CriticalPathHorizontal.noDesign")}</strong>
              <br />
              {d.disenoMuestra || "—"}
              {d.referenciaPedido ? (
                <>
                  <br />
                  Pedido: {d.referenciaPedido}
                  {d.idPedv ? ` (A3 #${d.idPedv})` : ""}
                </>
              ) : null}
            </>
          );
          return (
            <Tooltip title={tooltip} arrow placement="top">
              <Box sx={{ pl: 1, minWidth: 0, width: "100%" }}>
                <Typography
                  variant="caption"
                  color="#00c2a3"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 900,
                    pt: 0.5,
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.designCliente}
                </Typography>
                <Typography
                  variant="body2"
                  color="orange"
                  sx={{
                    fontSize: "0.7rem",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.disenoMuestra}
                </Typography>
                {d.referenciaPedido ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: 700,
                      color: "#7c4dff",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.referenciaPedido}
                  </Typography>
                ) : null}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "Units",
        headerName: t("CriticalPathHorizontal.gridHeaderUnits"),
        width: 60,
        pinned: "left",
        type: "number",
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ fontSize: "0.7rem", pt: 0.8, pr: 1 }}
          >
            {params.row?.Units != null
              ? new Intl.NumberFormat(numberLocale, {
                  maximumFractionDigits: 0,
                }).format(Number(params.row.Units))
              : ""}
          </Typography>
        ),
      },
      {
        field: "Fotos",
        headerName: t("CriticalPathHorizontal.gridHeaderPhotos"),
        width: 65,
        pinned: "left",
        renderCell: (params) => (
          <PhotosCell
            productId={params.row.productId}
            idMuestra={params.row.idMuestra}
            numeroPresupuesto={params.row?.NumeroPresupuesto}
            onImageClick={handleImageClick}
          />
        ),
      },
    ],
    [handleRequestToggleEntityClosed, t, numberLocale, getRowChatMeta, handleOpenNotas],
  );

  const logisticsColumnWidths = useMemo(
    () => ({
      POReference: 150,
      PODate: 95,
      Factory: 300,
      /* Remarks: 150, */
      NCartons: 85,
      GrossWeightKgs: 85,
      TotalVolumeCbm: 85,
      ShipmentMethod: 110,
      BookingDone: 100,
      Forwarder: 200,
      ShipmentCodeCustomer: 200,
      BLAWBNr: 200,
      ETA: 95,
      DocumentsApproved: 110,
      OriginalTelexDocsSemt: 110,
    }),
    [],
  );

  // Genera el resto de columnas dinámicamente (incluye logística)
  const columns = useMemo(() => {
    const taskColumns = defaultTaskOrder.map((task) => {
      const isPlaceholder = task.startsWith(PLACEHOLDER_PREFIX);
      const isLogistics =
        COLLAPSIBLE_GROUPS["Envío y logística"]?.includes(task);
      const customWidth = isLogistics ? logisticsColumnWidths[task] || 90 : 90;

      const sortProps = isPlaceholder
        ? { sortable: false }
        : isLogistics
          ? LOGISTICS_DATE_FIELDS.has(task)
            ? {
                valueGetter: (params) =>
                  getLogisticsSortTimestamp(params.row, task),
                sortComparator: compareNullableTimestamps,
              }
            : {
                valueGetter: (params) =>
                  getLogisticsFieldValue(params.row, task),
              }
          : {
              valueGetter: (params) =>
                getTimelineColumnSortTimestamp(params.row, task),
              sortComparator: compareNullableTimestamps,
            };

      return {
        field: task,
        headerName: isPlaceholder ? "" : task,
        width: isPlaceholder ? 5 : customWidth,
        hideable: !isPlaceholder,
        ...sortProps,
        renderCell: (params) =>
          isPlaceholder ? null : (
            <TaskCell
              row={params.row}
              task={task}
              taskObj={
                isLogistics
                  ? params.row[task]
                  : pickTimelineCell(params.row, task)
              }
              isLogistics={isLogistics}
              isDark={isDark}
            />
          ),
      };
    });

    return [...fixedColumns, ...taskColumns];
  }, [defaultTaskOrder, isDark, fixedColumns, logisticsColumnWidths]);

  const columnGroupingModel = useMemo(() => {
    return Object.entries(groupsWithPlaceholders).map(([groupId, fields]) => ({
      groupId,
      headerName: t(
        `CriticalPathHorizontal.groups.${GROUP_ID_TO_I18N[groupId]}`,
      ),
      renderHeaderGroup: (params) => (
        <CollapsibleHeaderGroup
          {...params}
          setColumnVisibilityModel={setColumnVisibilityModel}
        />
      ),
      children: fields.map((field) => ({ field })),
    }));
  }, [groupsWithPlaceholders, t]);

  const pageLoading = proveedorLoading || isLoading;

  const pageShellSx = {
    display: "flex",
    flexDirection: "column",
    minHeight: { xs: "100dvh", md: "calc(100vh - 100px)" },
    boxSizing: "border-box",
    background:
      theme.palette.mode === "dark"
        ? theme.palette.background.default
        : theme.palette.grey[50],
    pt: { xs: 1, md: 1.25 },
    px: { xs: 1.25, sm: 2 },
    pr: { xs: 1.25, sm: 2, md: 3 },
    pb: { xs: 10, md: 2 },
    minWidth: 0,
    overflow: "hidden",
    ...contentMargin,
  };

  const heroShellSx = {
    ...pageHeroShellSx(theme, { sticky: !isMobile }),
    mb: { xs: 1.25, md: 1 },
    flexShrink: 0,
    ...(theme.palette.mode === "light"
      ? {
          background: theme.palette.background.paper,
          borderColor: alpha(accent, 0.2),
          boxShadow: `0 10px 32px ${alpha(theme.palette.common.black, 0.08)}`,
        }
      : {}),
  };

  const filtersPanelSx = {
    ...pageContentCardSx(theme, accent, { highlighted: false }),
    p: { xs: 1.25, md: 1 },
    display: "grid",
    gridTemplateColumns: {
      xs: "repeat(2, minmax(0, 1fr))",
      md: "repeat(auto-fill, minmax(156px, 1fr))",
    },
    gap: 0.75,
    alignItems: "center",
  };

  const mobileVisibleRows = useMemo(
    () => filteredRows.slice(0, mobileRenderedCount),
    [filteredRows, mobileRenderedCount],
  );

  const renderMobileCpCard = (row) => {
    const rowKey = getCriticalPathDataGridRowId(row);
    const d = getCpDesignDisplay(row);
    const clientName = row.CLiente || t("CriticalPathHorizontal.noClient");
    const clientAlias = row?.MarcaNombre || "";
    const aliasIsDifferent =
      clientAlias &&
      clientAlias.trim().toUpperCase() !==
        String(clientName).trim().toUpperCase();
    const clientColor = getClientColor(clientName);
    const supplierName =
      row.ProveedorNombre || t("CriticalPathHorizontal.noSupplier");
    const supplierColor = getSupplierColor(supplierName);
    const active = isMobileRowActive(row);

    return (
      <Box
        key={rowKey}
        onClick={() => handleOpenGridRow(row)}
        sx={{
          ...pageMobileCardSx(theme, { active, accentColor: accent }),
          cursor: "pointer",
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Box
            sx={{
              width: 72,
              minWidth: 72,
              height: 72,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              bgcolor: alpha(theme.palette.background.default, 0.55),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <PhotosCell
              productId={row.productId}
              idMuestra={row.idMuestra}
              numeroPresupuesto={row?.NumeroPresupuesto}
              onImageClick={handleImageClick}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: clientColor,
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {getEntityInitial(clientName)}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {clientName}
                    {aliasIsDifferent ? ` · ${clientAlias}` : ""}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, lineHeight: 1.2 }}
                    noWrap
                  >
                    {d.designCliente || t("CriticalPathHorizontal.noDesign")}
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={0.25}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
              >
                {(() => {
                  const { noLeidos, hasMessages, hasMention } =
                    getRowChatMeta(row);
                  return (
                    <CpChatDot
                      noLeidos={noLeidos}
                      hasMessages={hasMessages}
                      hasMention={hasMention}
                      onClick={(e) => handleOpenChat(row, e)}
                    />
                  );
                })()}
                <NotasBadge
                  key={`${row.entityKey}-${row.NotasCount}-mobile`}
                  productId={row.productId}
                  idMuestra={row.idMuestra}
                  idPresupuestoA3Pedido={row.IdPresupuestoA3Pedido}
                  notasCount={row.NotasCount || 0}
                  onOpenDialog={handleOpenNotas}
                />
                <Tooltip
                  title={
                    row?.IsClosed
                      ? t("CriticalPathHorizontal.tooltipReopenCp")
                      : t("CriticalPathHorizontal.tooltipCloseCp")
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestToggleEntityClosed(row);
                    }}
                    sx={{
                      color: row?.IsClosed ? "error.main" : "success.main",
                    }}
                  >
                    {row?.IsClosed ? (
                      <VisibilityIcon fontSize="small" />
                    ) : (
                      <VisibilityOffIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                {row?.HasLegacyLogistics ? (
                  <Tooltip
                    title={t("CriticalPathHorizontal.tooltipLegacyLogistics")}
                  >
                    <LocalShippingIcon
                      sx={{ fontSize: 18, color: "warning.main" }}
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            </Stack>

            <Typography
              variant="body2"
              color="orange"
              sx={{ fontSize: "0.75rem", mt: 0.35 }}
              noWrap
            >
              {d.disenoMuestra || "—"}
            </Typography>

            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.65 }}
            >
              <Chip
                size="small"
                label={supplierName}
                sx={{
                  ...pageKpiChipSx,
                  maxWidth: "100%",
                  bgcolor: alpha(supplierColor, 0.14),
                  color: supplierColor,
                  border: "1px solid",
                  borderColor: alpha(supplierColor, 0.24),
                }}
              />
              {row.TemporadaNombre ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={row.TemporadaNombre}
                  sx={{ ...pageKpiChipSx, maxWidth: "100%" }}
                />
              ) : null}
              {row.Units != null && row.Units !== "" ? (
                <Chip
                  size="small"
                  label={new Intl.NumberFormat(numberLocale, {
                    maximumFractionDigits: 0,
                  }).format(Number(row.Units))}
                  sx={pageKpiChipSx}
                />
              ) : null}
              {row?.IsClosed ? (
                <Chip
                  size="small"
                  label={t("CriticalPathHorizontal.labelClosedCp")}
                  sx={{
                    ...pageKpiChipSx,
                    bgcolor: alpha(theme.palette.error.main, 0.12),
                    color: "error.dark",
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  if (pageLoading) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            ...pageShellSx,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={pageShellSx}>
        <Box sx={heroShellSx}>
          <Stack spacing={{ xs: 1.25, md: 0.875 }} sx={{ p: { xs: 1.25, md: 1, lg: 1.25 } }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { md: "flex-start" },
                justifyContent: "space-between",
                gap: { xs: 1, md: 1 },
              }}
            >
              <Stack
                direction="row"
                spacing={{ xs: 1.25, md: 1 }}
                alignItems="flex-start"
                sx={{ flex: 1, minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: { xs: 44, md: 36 },
                    height: { xs: 44, md: 36 },
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    bgcolor: alpha(accent, 0.12),
                    color: theme.palette.primary.dark,
                    border: "1px solid",
                    borderColor: alpha(accent, 0.24),
                  }}
                >
                  <RouteOutlinedIcon sx={{ fontSize: { xs: "1.25rem", md: "1.1rem" } }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      ...pageSectionTitleSx,
                      fontSize: { xs: "0.68rem", md: "0.62rem" },
                      lineHeight: 1.1,
                    }}
                  >
                    {t("CriticalPathHorizontal.chipCrm")}
                  </Typography>
                  <Typography
                    variant={isMobile ? "h5" : "h6"}
                    component="h1"
                    sx={{ fontWeight: 800, lineHeight: 1.1 }}
                  >
                    {t("CriticalPathHorizontal.title")}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${filteredRows.length}`}
                    sx={{
                      ...pageKpiChipSx,
                      mt: 0.5,
                      bgcolor: alpha(accent, 0.12),
                      color: "primary.dark",
                      border: "1px solid",
                      borderColor: alpha(accent, 0.2),
                    }}
                  />
                </Box>
              </Stack>
            </Box>

            {isMobile ? (
              <TextField
                size="small"
                placeholder={t("CriticalPathHorizontal.searchPlaceholder")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={filterFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            ) : null}

            <Box sx={filtersPanelSx}>
              {!isMobile ? (
                <TextField
                  size="small"
                  label={t("CriticalPathHorizontal.labelSearch")}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    ...filterFieldSx,
                    gridColumn: { md: "span 2" },
                    maxWidth: { md: 320 },
                  }}
                />
              ) : null}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showWithOriginalTelex}
                    onChange={(e) => setShowWithOriginalTelex(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("CriticalPathHorizontal.labelProjectsFinished")}
                  </Typography>
                }
                sx={{
                  m: 0,
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                  alignSelf: "center",
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showClosedEntities}
                    onChange={(e) => setShowClosedEntities(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t("CriticalPathHorizontal.labelClosedCp")}
                  </Typography>
                }
                sx={{
                  m: 0,
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                  alignSelf: "center",
                }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<InfoOutlinedIcon />}
                onClick={(e) => setLegendAnchorEl(e.currentTarget)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  gridColumn: { xs: "1 / -1", sm: "auto" },
                }}
              >
                {t("CriticalPathHorizontal.legend")}
              </Button>
            </Box>
          </Stack>
        </Box>

        <Popover
          open={legendOpen}
          anchorEl={legendAnchorEl}
          onClose={() => setLegendAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              p: 1.5,
              mt: 0.5,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              minWidth: 240,
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            {t("CriticalPathHorizontal.legend")}
          </Typography>
          <Stack spacing={0.85}>
            {legendItems.map((it) => (
              <Box
                key={it.label}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: it.textOnly ? "transparent" : it.color,
                    color: it.textOnly ? it.color : undefined,
                    border: it.textOnly
                      ? `1px solid ${alpha(theme.palette.divider, 0.6)}`
                      : "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {it.textOnly || ""}
                </Box>
                <Typography variant="body2">{it.label}</Typography>
              </Box>
            ))}
          </Stack>
        </Popover>
        {!filteredRows.length ? (
          <Typography sx={{ mt: 2, px: 0.5 }}>
            {t("CriticalPathHorizontal.emptyData")}
          </Typography>
        ) : isMobile ? (
          <Stack
            spacing={1}
            sx={{ flex: 1, minHeight: 0, overflow: "auto", pt: 0.25 }}
          >
            {mobileVisibleRows.map((row) => renderMobileCpCard(row))}
            {mobileRenderedCount < filteredRows.length ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setMobileRenderedCount((prev) =>
                    Math.min(prev + MOBILE_RENDER_CHUNK, filteredRows.length),
                  )
                }
                sx={{ alignSelf: "center", fontWeight: 700 }}
              >
                {t("CriticalPathHorizontal.loadMore30")}
              </Button>
            ) : null}
          </Stack>
        ) : (
        <DataGridPro
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => {
            // Misma lógica que getCriticalPathDataGridRowId (ver allí).
            if (row.id != null && String(row.id).trim() !== "") {
              return String(row.id);
            }
            if (
              row.idPresupuestoCRM != null &&
              Number.isFinite(Number(row.idPresupuestoCRM))
            ) {
              return `PRESUPUESTO_${Number(row.idPresupuestoCRM)}`;
            }
            const pid = row.productId ?? row.ProductId;
            if (pid !== null && pid !== undefined && String(pid).trim() !== "") {
              return `P_${Number(pid)}`;
            }
            const mid = row.idMuestra ?? row.ID_MUESTRA;
            if (mid !== null && mid !== undefined && String(mid).trim() !== "") {
              return `M_${Number(mid)}`;
            }
            return row.entityKey;
          }}
          disableRowSelectionOnClick
          density="standard"
          onRowClick={(params) => {
            const stableId =
              params.id != null && String(params.id).trim() !== ""
                ? String(params.id)
                : "";
            const fromApiModel =
              stableId && typeof params.api?.getRow === "function"
                ? params.api.getRow(stableId)
                : null;
            const row =
              (stableId &&
                filteredRows.find(
                  (r) => getCriticalPathDataGridRowId(r) === stableId,
                )) ||
              fromApiModel ||
              params.row;
            handleOpenGridRow(row, stableId);
          }}
          rowHeight={66}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 20 } },
            pinnedColumns: {
              left: [
                "Chat",
                "Notas",
                "cerrar",
                "legacyLog",
                "CLiente",
                "TemporadaNombre",
                "ProveedorNombre",
                "designCliente",
                "Units",
                "Fotos",
              ],
              right: [],
            },
          }}
          columnGroupingModel={columnGroupingModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) => {
            setColumnVisibilityModel(newModel);
            localStorage.setItem(
              "criticalPathColumnVisibility",
              JSON.stringify(newModel),
            );
          }}
          experimentalFeatures={{ columnGrouping: true }}
          sx={{
            flex: 1,
            minHeight: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            boxShadow: isDark
              ? "0 4px 20px rgba(0,0,0,0.35)"
              : "0 4px 20px rgba(15,23,42,0.06)",
            overflow: "hidden",

            // 🔹 Cabeceras
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.72rem",
              padding: "0px",
              borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            },
            "& .MuiDataGrid-columnHeader--filledGroup": {
              fontSize: "0.72rem",
              padding: "0px",
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              textAlign: "center",
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: 0.3,
            },
            "& .MuiDataGrid-columnHeader": {
              fontSize: "0.72rem",
              padding: "0px",
              backgroundColor: isDark ? "#370000" : "#eee",
              textAlign: "center",
              transition: "background-color 0.15s ease",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
            "& .MuiDataGrid-columnHeader--emptyGroup": {
              fontSize: "0.72rem",
              pl: 1,
              padding: "0px",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: alpha(theme.palette.divider, 0.6),
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              backgroundColor: "transparent",
              pl: 1,
              pr: 1.5,
            },

            // 🔹 Celdas
            "& .MuiDataGrid-cell": {
              padding: "0px",
              borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },

            // 🔹 Filas: hover suave + cursor pointer (drawer al click)
            "& .MuiDataGrid-row": {
              transition: "background-color 0.15s ease",
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: alpha(
                theme.palette.primary.main,
                isDark ? 0.08 : 0.04,
              ),
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: alpha(
                theme.palette.primary.main,
                isDark ? 0.16 : 0.1,
              ),
              "&:hover": {
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  isDark ? 0.22 : 0.14,
                ),
              },
            },

            // 🔹 Columnas pinneadas: borde derecho diferenciador
            "& .MuiDataGrid-pinnedColumns": {
              boxShadow: isDark
                ? `4px 0 12px ${alpha("#000", 0.45)}`
                : `4px 0 12px ${alpha("#0f172a", 0.06)}`,
              backgroundColor: theme.palette.background.paper,
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              boxShadow: isDark
                ? `4px 0 12px ${alpha("#000", 0.45)}`
                : `4px 0 12px ${alpha("#0f172a", 0.06)}`,
              backgroundColor: theme.palette.background.paper,
            },

            // 🔹 Footer / paginación
            "& .MuiDataGrid-footerContainer": {
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(
                theme.palette.primary.main,
                isDark ? 0.06 : 0.03,
              ),
              minHeight: 44,
            },
            "& .MuiTablePagination-root": {
              fontSize: "0.78rem",
              fontWeight: 600,
            },

            // 🔹 Scrollbar
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              height: 10,
              width: 10,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(
                theme.palette.text.primary,
                isDark ? 0.25 : 0.15,
              ),
              borderRadius: 8,
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
              backgroundColor: alpha(
                theme.palette.text.primary,
                isDark ? 0.4 : 0.3,
              ),
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },

            ...defaultTaskOrder.reduce((acc, task) => {
              let color = getHeaderColor(task, theme.palette.mode);

              if (task.startsWith(PLACEHOLDER_PREFIX)) {
                const groupName = task
                  .replace(PLACEHOLDER_PREFIX, "")
                  .replace(/_/g, " ");
                color = GROUP_COLOR_MAP[groupName] || "#eeeeee";

                acc[`.MuiDataGrid-columnHeader[data-field="${task}"]`] = {
                  backgroundColor: color,
                  minWidth: 4,
                  maxWidth: 4,

                  border: "none",
                  pointerEvents: "none",
                };
                acc[`.MuiDataGrid-cell[data-field="${task}"]`] = {
                  backgroundColor: color,
                  minWidth: 4,
                  maxWidth: 4,

                  border: "none",
                  pointerEvents: "none",
                };
              } else {
                /* color de cada grupo */
                acc[`.MuiDataGrid-columnHeader[data-field="${task}"]`] = {
                  backgroundColor: color,
                };
              }

              return acc;
            }, {}),
          }}
        />
        )}

        <Popover
          open={Boolean(anchorEl && hoveredImage)}
          anchorEl={anchorEl}
          onClose={() => {
            setAnchorEl(null);
            setHoveredImage(null);
          }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
        >
          {hoveredImage && (
            <Box sx={{ p: 1 }}>
              <img
                src={hoveredImage}
                alt="preview"
                style={{
                  width: 300,
                  height: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}
        </Popover>
      </Box>

      {drawerOpen && drawerMode && (
        <CriticalPathDrawer
          key={`${drawerMode}_${selectedProductId ?? ""}_${selectedMuestraId ?? ""}_${selectedIdPresupuesto ?? ""}_${selectedIdPresupuestoA3Pedido ?? ""}`}
          open={drawerOpen}
          mode={drawerMode}
          productId={selectedProductId}
          idMuestra={selectedMuestraId}
          idPresupuesto={selectedIdPresupuesto}
          idPresupuestoA3Pedido={selectedIdPresupuestoA3Pedido}
          gridRowSnapshot={selectedGridRowSnapshot}
          legacyLogistics={selectedLegacyLogistics}
          hasLegacyLogisticsRow={selectedHasLegacyLogistics}
          onDirty={() => {
            /* No recargar todo el grid al editar en el drawer (evita vaciar filas visibles). */
          }}
          onClose={(updatedEntity) => {
            const closedProdId = selectedProductId;
            const closedPresupuestoId = selectedIdPresupuesto;
            const closedPedidoId = selectedIdPresupuestoA3Pedido;
            const closedMuestraId = selectedMuestraId;

            if (
              updatedEntity?.changed &&
              Array.isArray(updatedEntity.timeline) &&
              updatedEntity.timeline.length > 0
            ) {
              setRows((prev) =>
                prev.map((row) => {
                  if (closedMuestraId != null) {
                    const rowMid = row.idMuestra ?? row.ID_MUESTRA;
                    if (Number(rowMid) !== Number(closedMuestraId)) return row;
                  } else {
                    const rowId = getCriticalPathDataGridRowId(row);
                    const rowPed =
                      row.IdPresupuestoA3Pedido ??
                      parseIdPresupuestoA3PedidoFromRowId(rowId);
                    const rowPres =
                      row.idPresupuestoCRM ??
                      parseIdPresupuestoFromRowId(rowId);
                    if (
                      (row.ProductId ?? row.productId) !== closedProdId
                    ) {
                      return row;
                    }
                    if ((closedPedidoId ?? null) !== (rowPed ?? null)) {
                      return row;
                    }
                    if (
                      closedPresupuestoId != null &&
                      rowPres !== closedPresupuestoId
                    ) {
                      return row;
                    }
                  }
                  return applyTimelineTasksToCpRow(
                    row,
                    updatedEntity.timeline,
                  );
                }),
              );
            }

            setDrawerOpen(false);
            setDrawerMode(null);
            setSelectedProductId(null);
            setSelectedMuestraId(null);
            setSelectedIdPresupuesto(null);
            setSelectedIdPresupuestoA3Pedido(null);
            setSelectedGridRowSnapshot(null);
            setSelectedLegacyLogistics([]);
            setSelectedHasLegacyLogistics(false);
            if (updatedEntity?.changed) {
              fetchAndSetRows();
            }
          }}
        />
      )}
      <CpEntityChat
        open={cpChatState.open}
        productId={cpChatState.productId}
        idPresupuestoA3Pedido={cpChatState.idPresupuestoA3Pedido}
        title={cpChatState.title}
        subtitle={cpChatState.subtitle}
        initialTab={cpChatState.initialTab}
        onNotasChanged={({ changed, productId, idMuestra, idPresupuestoA3Pedido }) => {
          if (changed) {
            refreshCpNotasCount({
              productId,
              idMuestra,
              idPresupuestoA3Pedido,
            });
          }
        }}
        onClose={() => {
          const {
            productId,
            idPresupuestoA3Pedido,
          } = cpChatState;
          setCpChatState({
            open: false,
            productId: null,
            idPresupuestoA3Pedido: null,
            title: "",
            subtitle: "",
            initialTab: "notas",
          });
          refreshCpNotasCount({
            productId,
            idMuestra: null,
            idPresupuestoA3Pedido,
          });
          loadChatSummary();
        }}
      />

      <Dialog
        open={closeConfirmState.open}
        onClose={() => setCloseConfirmState({ open: false, row: null })}
      >
        <DialogTitle>
          {t("CriticalPathHorizontal.closeDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("CriticalPathHorizontal.closeDialogBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseConfirmState({ open: false, row: null })}>
            {t("CriticalPathHorizontal.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmCloseEntity}
          >
            {t("CriticalPathHorizontal.closeLine")}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CriticalPathHorizontal;
